import React, { useMemo } from "react";
import { Item } from "@exiftrator/shared/components/ui";
import { useAppContext } from "../../contexts/AppContext";
import { useSelection } from "../../hooks/useSelection";
import { useFiles } from "../../hooks/useFiles";
import { useFileListProjection } from "../../hooks/useFileListProjection";
import { useFileQueueInteractions } from "./useFileQueueInteractions";
import FileQueueList from "./FileQueueList";
import { PanelHeader } from "../PanelHeader";
import { Thumbnail } from "../Thumbnail";
import { EmptyState } from "../EmptyState";
import { formatDisplayDateTime } from "../../utils/dateUtils";
import { getDateStatus } from "./fileListProjectionUtils";
import FileListControls from "./FileListControls";
import { useIsMobile, useIsTablet } from "../../hooks/useMobile";
import {
  WORKSPACE_SPACING,
  getDensityClass,
} from "../Workspace/workspaceSpacing";
import { cn } from "../utils/cn";

function getQueueDateLabel(file) {
  const dateStatus = getDateStatus(file);
  if (dateStatus === "with-date") {
    return formatDisplayDateTime(file.modifiedExifData?.dateTaken, "No date", {
      includeSeconds: true,
      yearFormat: "2-digit",
    });
  }
  return dateStatus === "invalid-date" ? "Invalid date" : "No date";
}

function StandardQueuePane({
  headerContent,
  horizontal = false,
  showListControls = true,
}) {
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const statusStripInsetXClass = getDensityClass(
    isMobile,
    isTablet,
    WORKSPACE_SPACING.statusStripInsetX,
  );
  const {
    files,
    fileListSort,
    setFileListSort,
    fileListFilters,
    setFileListFilters,
  } = useAppContext();
  const { addFiles } = useFiles();
  const {
    selectedIndices,
    primaryIndex,
    selectSingle,
    toggleSelect,
    rangeSelect,
  } = useSelection();

  const { visibleIndices, totalCount, visibleCount } = useFileListProjection({
    files,
    sort: fileListSort,
    filters: fileListFilters,
  });

  const {
    scrollContainerRef,
    handleFileClick,
    selectByKeyboard,
    handleDragStart,
    handleDrop,
    handleDragOver,
  } = useFileQueueInteractions({
    files,
    selectedIndices,
    primaryIndex,
    selectSingle,
    toggleSelect,
    rangeSelect,
    addFiles,
  });

  const sections = useMemo(
    () => [
      {
        id: "files",
        rows: visibleIndices.map((index) => ({ index, file: files[index], badges: [] })),
      },
    ],
    [files, visibleIndices],
  );

  const controls = showListControls ? (
    <FileListControls
      sort={fileListSort}
      onSortChange={setFileListSort}
      filters={fileListFilters}
      onFiltersChange={setFileListFilters}
    />
  ) : null;

  const footerLabel = [
    `Showing ${visibleCount} of ${totalCount} file${totalCount === 1 ? "" : "s"}`,
    `${selectedIndices.size} selected`,
  ].join(" \u2022 ");

  if (horizontal) {
    return (
      <section className="flex h-full min-h-0 flex-col bg-bg-secondary">
        <PanelHeader
          tone="contrast"
          title=""
          leftContent={headerContent || "Files"}
          rightContent={controls}
        />

        <div
          className={cn(
            "flex-1 overflow-x-auto overflow-y-hidden",
            WORKSPACE_SPACING.listRailInset,
          )}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
        >
          {visibleCount === 0 ? (
            <div className="h-full min-h-[180px]">
              <EmptyState
                icon="🎞️"
                title={files.length === 0 ? "Drag archives or click Select to begin" : "No files match current filters"}
                className="h-full"
              />
            </div>
          ) : (
            <div className="flex min-w-max gap-3">
              {visibleIndices.map((index) => {
                const file = files[index];
                const displayDate = getQueueDateLabel(file);

                return (
                  <Item
                    key={index}
                    draggable={true}
                    onDragStart={(event) => handleDragStart(event, index)}
                    onClick={(event) => handleFileClick(index, event)}
                    selected={selectedIndices.has(index)}
                    className="w-[160px] shrink-0 flex-col items-start gap-2 p-2"
                    title={file.name}
                  >
                    <div className="relative">
                      <Thumbnail
                        src={file.thumbnailUrl}
                        alt={file.name}
                        size="preview"
                        selected={selectedIndices.has(index)}
                      />
                      {file.hasChanges && (
                        <span
                          className="absolute right-0 top-0 size-2 rounded-full border border-bg-secondary bg-accent-primary shadow-xs"
                          title="Edited (unsaved changes)"
                          aria-label="Edited (unsaved changes)"
                        />
                      )}
                    </div>
                    <div className="min-w-0">
                      <div className="truncate text-xs font-semibold text-foreground" title={file.name}>
                        {file.name}
                      </div>
                      <div className="truncate text-[11px] font-mono text-foreground/70" title={displayDate}>
                        {displayDate}
                      </div>
                    </div>
                  </Item>
                );
              })}
            </div>
          )}
        </div>

        {files.length > 0 && (
          <div
            className={cn(
              "border-t border-border-subtle text-xs text-text-secondary",
              WORKSPACE_SPACING.statusStripInsetY,
              statusStripInsetXClass,
            )}
          >
            {footerLabel}
          </div>
        )}
      </section>
    );
  }

  return (
    <aside className="flex h-full min-h-0 w-[300px] flex-col border-r border-border-default bg-bg-secondary">
      <PanelHeader
        tone="contrast"
        title=""
        leftContent={headerContent}
        rightContent={controls}
      />

      <FileQueueList
        sections={sections}
        selectedIndices={selectedIndices}
        primaryIndex={primaryIndex}
        onItemClick={handleFileClick}
        onItemDragStart={handleDragStart}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onKeyboardSelect={selectByKeyboard}
        scrollContainerRef={scrollContainerRef}
        emptyTitle={files.length === 0 ? "Drag archives or click Select to begin" : "No files match current filters"}
      />

      {files.length > 0 && (
        <div
          className={cn(
            "border-t border-border-subtle text-xs text-text-secondary",
            WORKSPACE_SPACING.statusStripInsetY,
            statusStripInsetXClass,
          )}
        >
          {footerLabel}
        </div>
      )}
    </aside>
  );
}

export default StandardQueuePane;
